import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

const libphonenumber = require("google-libphonenumber");
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
const PhoneNumberType = libphonenumber.PhoneNumberType;

const DEFAULT_RANGE = { min: 7, max: 15 };

export interface PhoneDigitRange {
  min: number;
  max: number;
}

function toLengthArray(desc: any): number[] {
  if (!desc || typeof desc.possibleLengthCount !== "function" || desc.possibleLengthCount() === 0) {
    return [];
  }
  const lengths: number[] = [];
  for (let i = 0; i < desc.possibleLengthCount(); i++) {
    lengths.push(desc.getPossibleLength(i));
  }
  return lengths;
}

export function getIso2FromDialCode(dialCode: string): string {
  const digits = String(dialCode || "").replace(/\D/g, "");
  if (!digits) {
    return "";
  }
  try {
    const iso2 = phoneUtil.getRegionCodeForCountryCode(Number(digits));
    return iso2 && iso2 !== "ZZ" ? iso2 : "";
  } catch {
    return "";
  }
}

function exampleLength(iso2: string, type: number): number | null {
  try {
    const example = phoneUtil.getExampleNumberForType(iso2, type);
    return example ? String(example.getNationalNumber()).length : null;
  } catch {
    return null;
  }
}

/**
 * National-number length for a country (ISO 3166-1 alpha-2), covering
 * mobile and landline. With +971 selected (no leading 0):
 * UAE landline is 8 digits (e.g. 41234567), UAE mobile is 9 digits (e.g. 501234567).
 */
export function getMobileDigitRange(iso2: string): PhoneDigitRange {
  if (!iso2) {
    return { ...DEFAULT_RANGE };
  }

  try {
    const region = iso2.toUpperCase();
    const meta = phoneUtil.getMetadataForRegion(region);
    const lengths = toLengthArray(meta && meta.getMobile())
      .concat(toLengthArray(meta && meta.getFixedLine()));

    if (lengths.length) {
      return { min: Math.min.apply(null, lengths), max: Math.max.apply(null, lengths) };
    }

    const exampleLengths = [
      exampleLength(region, PhoneNumberType.MOBILE),
      exampleLength(region, PhoneNumberType.FIXED_LINE),
    ].filter((len) => len != null) as number[];

    if (exampleLengths.length) {
      return {
        min: Math.min.apply(null, exampleLengths),
        max: Math.max.apply(null, exampleLengths),
      };
    }

    const generalLengths = toLengthArray(meta && meta.getGeneralDesc());
    if (generalLengths.length) {
      return { min: Math.min.apply(null, generalLengths), max: Math.max.apply(null, generalLengths) };
    }
  } catch {
    // fall through to default
  }

  return { ...DEFAULT_RANGE };
}

export function getMobileDigitRangeForDialCode(dialCode: string): PhoneDigitRange {
  return getMobileDigitRange(getIso2FromDialCode(dialCode));
}

export function getPhoneLengthErrorMessage(range: PhoneDigitRange): string {
  if (range.min === range.max) {
    return `Please enter a valid phone number with exactly ${range.min} digits.`;
  }
  return `Please enter a valid phone number with ${range.min}-${range.max} digits.`;
}

export function getPhoneLengthErrorMessageForDialCode(dialCode: string): string {
  return getPhoneLengthErrorMessage(getMobileDigitRangeForDialCode(dialCode));
}

export function isValidMobileForCountry(nationalNumber: string, iso2: string): boolean {
  if (!nationalNumber || !iso2) {
    return false;
  }
  const region = iso2.toUpperCase();

  try {
    const parsed = phoneUtil.parse(nationalNumber, region);
    if (phoneUtil.isValidNumberForRegion(parsed, region)) {
      return true;
    }
  } catch {
    // fall through to national-pattern checks
  }

  try {
    const meta = phoneUtil.getMetadataForRegion(region);
    if (!meta) {
      return false;
    }
    return (
      matchesNationalDesc(nationalNumber, meta.getMobile()) ||
      matchesNationalDesc(nationalNumber, meta.getFixedLine(), true)
    );
  } catch {
    return false;
  }
}

function matchesNationalDesc(nationalNumber: string, desc: any, relaxLandline: boolean = false): boolean {
  if (!desc) {
    return false;
  }
  const pattern = typeof desc.getNationalNumberPattern === "function" ? desc.getNationalNumberPattern() : "";
  if (pattern && new RegExp("^" + pattern + "$").test(nationalNumber)) {
    return true;
  }
  if (!relaxLandline) {
    return false;
  }
  const lengths = toLengthArray(desc);
  if (lengths.length && lengths.indexOf(nationalNumber.length) === -1) {
    return false;
  }
  // Dubai chart landlines are 4 + 7 digits (e.g. 41234567). libphonenumber
  // also constrains the second digit, which rejects valid local examples.
  const firstClassMatch = String(pattern || "").match(/^(\[[^\]]+\]|\d)/);
  if (!firstClassMatch) {
    return lengths.indexOf(nationalNumber.length) !== -1;
  }
  return new RegExp("^" + firstClassMatch[1] + "\\d{" + (nationalNumber.length - 1) + "}$").test(nationalNumber);
}

/**
 * Validates a national phone number (mobile or landline) against the selected
 * country dial code (sibling `countryCode` / `spocCountryCode`, or a getter).
 */
export function countryMobilePhoneValidator(
  getDialCode?: () => string,
  options: { required?: boolean; dialCodeControl?: string } = {},
): ValidatorFn {
  const required = options.required !== false;
  const dialCodeControl = options.dialCodeControl || "countryCode";

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value == null ? "" : String(control.value).trim();
    if (!value) {
      return required ? { required: true } : null;
    }
    if (!/^\d+$/.test(value)) {
      return { invalidPhone: true };
    }
    if (value.startsWith("0")) {
      return { cannotStartWithZero: true };
    }

    const parentDial = control.parent ? control.parent.get(dialCodeControl)?.value : "";
    const dialCode = getDialCode ? getDialCode() : parentDial;
    const iso2 = getIso2FromDialCode(dialCode);
    const range = getMobileDigitRange(iso2);

    if (value.length < range.min || value.length > range.max) {
      return { invalidPhone: true, min: range.min, max: range.max };
    }

    if (iso2 && !isValidMobileForCountry(value, iso2)) {
      return { invalidPhone: true, min: range.min, max: range.max };
    }

    return null;
  };
}
